import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon, ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import UrbanPartnerSelector from "../../components/CreateOrder/UrbanPartnerSelector";
import ProductSelector from "../../components/CreateOrder/ProductSelector";
import OrderSummary from "../../components/CreateOrder/OrderSummary";
import DeliveryMethodSelector from "../../components/CreateOrder/DeliveryMethodSelector";
import useCreateSeederOrder from "../../services/useCreateSeederOrder";
import {
  ISeederOrderItem,
  IUrbanPartnerSimple,
  IProductForOrder,
  IDeliveryMethod,
} from "../../types/seederOrders";

interface OrderItem extends ISeederOrderItem {
  product: IProductForOrder;
}

const CreateOrder = () => {
  const navigate = useNavigate();
  const [selectedUrbanPartner, setSelectedUrbanPartner] =
    useState<IUrbanPartnerSimple | null>(null);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] =
    useState<IDeliveryMethod | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createOrderMutation = useCreateSeederOrder();

  const handleAddProduct = (product: IProductForOrder, amount: number) => {
    const existingItemIndex = orderItems.findIndex(
      (item) => item.product_id === product.id,
    );

    if (existingItemIndex >= 0) {
      // Atualizar quantidade do produto existente
      const newItems = [...orderItems];
      newItems[existingItemIndex].amount += amount;
      setOrderItems(newItems);
    } else {
      // Adicionar novo produto
      setOrderItems([
        ...orderItems,
        {
          product_id: product.id,
          amount,
          product,
        },
      ]);
    }
  };

  const handleUpdateAmount = (productId: number, amount: number) => {
    if (amount <= 0) {
      handleRemoveItem(productId);
      return;
    }

    const newItems = orderItems.map((item) =>
      item.product_id === productId ? { ...item, amount } : item,
    );
    setOrderItems(newItems);
  };

  const handleRemoveItem = (productId: number) => {
    setOrderItems(orderItems.filter((item) => item.product_id !== productId));
  };

  const handleSubmit = () => {
    if (!selectedUrbanPartner) {
      setError("Por favor, selecione uma parceira urbana");
      return;
    }

    if (!selectedDeliveryMethod) {
      setError("Por favor, selecione um método de entrega");
      return;
    }

    if (orderItems.length === 0) {
      setError("Por favor, adicione pelo menos um produto");
      return;
    }

    createOrderMutation.mutate(
      {
        urban_partner_id: selectedUrbanPartner.id,
        delivery_method_id: selectedDeliveryMethod.id,
        address: selectedUrbanPartner.address,
        items: orderItems.map((item) => ({
          product_id: item.product_id,
          amount: item.amount,
        })),
      },
      {
        onSuccess: () => {
          setShowSuccess(true);
          // Resetar formulário
          setSelectedUrbanPartner(null);
          setSelectedDeliveryMethod(null);
          setOrderItems([]);

          // Redirecionar após 2 segundos
          setTimeout(() => {
            navigate("/app/seeder-orders");
          }, 2000);
        },
        onError: (err: any) => {
          setError(
            err?.items ||
              err?.message ||
              "Erro ao criar pedido. Tente novamente.",
          );
        },
      },
    );
  };

  const calculateSubtotal = () => {
    return orderItems.reduce((sum, item) => {
      return sum + parseFloat(item.product.price) * item.amount;
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const deliveryFee = selectedDeliveryMethod
      ? parseFloat(selectedDeliveryMethod.fee)
      : 0;
    return subtotal + deliveryFee;
  };

  // Calcula quanto de cada produto já foi adicionado ao pedido
  const getProductAmountInCart = (productId: number): number => {
    const item = orderItems.find((item) => item.product_id === productId);
    return item ? item.amount : 0;
  };

  // Calcula o estoque disponível para um produto
  const getAvailableStock = (product: IProductForOrder): number => {
    if (product.unlimited_stock) {
      return Infinity;
    }
    const amountInCart = getProductAmountInCart(product.id);
    return product.stock - amountInCart;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/app/seeder-orders")}
          variant="outlined"
        >
          Voltar
        </Button>
        <Typography variant="h4" component="h1">
          Criar Novo Pedido
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Seletor de Parceira Urbana */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              1. Selecione a Parceira Urbana
            </Typography>
            <UrbanPartnerSelector
              selectedPartner={selectedUrbanPartner}
              onSelectPartner={setSelectedUrbanPartner}
            />
          </CardContent>
        </Card>

        {/* Seletor de Método de Entrega */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              2. Selecione o Método de Entrega
            </Typography>
            <DeliveryMethodSelector
              selectedMethod={selectedDeliveryMethod}
              onSelectMethod={setSelectedDeliveryMethod}
              disabled={!selectedUrbanPartner}
            />
          </CardContent>
        </Card>

        {/* Seletor de Produtos */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              3. Adicione Produtos
            </Typography>
            <ProductSelector
              onAddProduct={handleAddProduct}
              disabled={!selectedUrbanPartner}
              getAvailableStock={getAvailableStock}
            />
          </CardContent>
        </Card>

        {/* Resumo do Pedido */}
        {orderItems.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                4. Resumo do Pedido
              </Typography>
              <OrderSummary
                items={orderItems}
                onUpdateAmount={handleUpdateAmount}
                onRemoveItem={handleRemoveItem}
                subtotal={calculateSubtotal()}
                deliveryFee={
                  selectedDeliveryMethod
                    ? parseFloat(selectedDeliveryMethod.fee)
                    : 0
                }
                deliveryMethod={selectedDeliveryMethod}
                total={calculateTotal()}
              />
            </CardContent>
          </Card>
        )}

        {/* Botão de Submissão */}
        {orderItems.length > 0 &&
          selectedUrbanPartner &&
          selectedDeliveryMethod && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setOrderItems([]);
                  setSelectedUrbanPartner(null);
                  setSelectedDeliveryMethod(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  createOrderMutation.isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <AddIcon />
                  )
                }
                onClick={handleSubmit}
                disabled={createOrderMutation.isLoading}
              >
                {createOrderMutation.isLoading ? "Criando..." : "Criar Pedido"}
              </Button>
            </Box>
          )}
      </Box>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Pedido criado com sucesso!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateOrder;
